import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

interface AddTeamMemberModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: { email: string; fullname: string }) => void;
    isSubmitting?: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AddTeamMemberModal: React.FC<AddTeamMemberModalProps> = ({
    isOpen,
    onClose,
    onSave,
    isSubmitting
}) => {
    const [fullname, setFullname] = useState('');
    const [email, setEmail] = useState('');

    const isEmailValid = EMAIL_REGEX.test(email);
    const isValid = fullname.trim().length >= 1 && isEmailValid;

    useEffect(() => {
        if (isOpen) {
            setFullname('');
            setEmail('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const modalContent = (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-xl w-full max-w-[520px] p-6 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900">Add team member</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-800">
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <p className="text-sm text-gray-500 mb-4">
                    A welcome email with a password reset link will be sent to the new member.
                </p>

                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-gray-700">Full name</label>
                        <input
                            value={fullname}
                            onChange={(e) => setFullname(e.target.value)}
                            placeholder="e.g., Jane Smith"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-gray-700">Email address</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="e.g., jane@example.com"
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                                email && !isEmailValid
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-300 focus:ring-blue-500'
                            }`}
                        />
                        {email && !isEmailValid && (
                            <p className="text-red-500 text-sm mt-1">
                                Please enter a valid email address
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-semibold text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        disabled={!isValid || isSubmitting}
                        onClick={() => onSave({ email, fullname })}
                        className={`px-4 py-2 text-sm font-bold text-white rounded-lg flex items-center gap-2 ${
                            isValid && !isSubmitting
                                ? 'bg-gray-800 hover:bg-gray-700'
                                : 'bg-gray-300 cursor-not-allowed'
                        }`}
                    >
                        {isSubmitting && (
                            <span className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        )}
                        Add member
                    </button>
                </div>
            </div>
        </div>
    );

    const modalRoot = document.getElementById('modal-root');
    return modalRoot ? ReactDOM.createPortal(modalContent, modalRoot) : null;
};

export default AddTeamMemberModal;
